import React from "react";
import { useTranslation } from "react-i18next";
import { tailwindClassnames } from "../common/TailwindClassnames";

type ReviewCardBodyProps = {
  /**
   * The chip / header row. Callers own it (and its bottom margin) so each
   * surface keeps its own badges: an "AI suggested" badge + confidence for a
   * suggestion, or the green "✓ accepted" chip + type / source chips for a
   * committed observation.
   */
  header: React.ReactNode;
  /** Resolved species / common name shown on the Species line. */
  speciesName: string;
  /**
   * Scientific (Latin) name (AKL-698). Rendered in italics after the species
   * name, but only when present and different from the display name — for a
   * taxon with no common name the API already falls display_name back to the
   * scientific name, and we must not print it twice.
   */
  scientificName?: string | null;
  /**
   * Extra content on the Species line, to the right of the name (e.g. the
   * observation-type text for a suggestion). Keep this light so the species +
   * Latin name have as much room as possible — row controls belong in
   * `countTrailing`.
   */
  speciesTrailing?: React.ReactNode;
  /**
   * Extra content right-aligned on the Count line — e.g. the Edit / Delete icon
   * buttons for a committed observation. Moving controls here keeps the whole
   * Species line free for the name + Latin name. When there is no `count`, the
   * Count line still renders (label/value omitted) so this content has a home.
   */
  countTrailing?: React.ReactNode;
  /**
   * Individual count. The Count line renders when this is set OR when
   * `countTrailing` is provided; it is omitted only when both are absent.
   */
  count?: number | string | null;
  /** Tooltip for the count value (shown because it can truncate when tight). */
  countTitle?: string;
  /** Rejected treatment: strike through the species name and count value. */
  strikethrough?: boolean;
  /**
   * A full-width line below the Species / Count block and above `actions` —
   * e.g. the AKL-780 model provenance line ("Models agree: SpeciesNet,
   * DeepFaune"). Deliberately outside the uppercase label column, so it is not
   * squeezed into the two-column grid the Species and Count lines share, and
   * deliberately outside `strikethrough`: that treatment marks a reviewer's
   * decision about the species and count, not a statement of fact about which
   * models ran. The caller owns its top margin, as with `header`.
   */
  footer?: React.ReactNode;
  /** Action row rendered below the Count line (e.g. Accept / Reject / Edit). */
  actions?: React.ReactNode;
};

const labelClasses =
  "w-16 shrink-0 text-[11px] uppercase tracking-wide text-off-black-light/60 dark:text-primary-off-white/40";

/**
 * Shared body for the review panel's species cards (AKL-698). Both the
 * suggestion card and the committed-observation row render through this so the
 * Species / Count block reads identically across the two — the only differences
 * are the caller-supplied header chips (an "accepted" chip on observations, the
 * stronger "rejected" treatment on rejected suggestions) and the actions slot.
 *
 * The species name sits one step smaller than the panel default with the Latin
 * name in italics beside it; the count always drops to its own line so a long
 * species name never squeezes it.
 *
 * `footer` (AKL-780) is an optional full-width line between the Count line and
 * `actions`. It renders nothing at all when absent, so surfaces that don't use
 * it — the committed-observation rows — are unaffected.
 */
const ReviewCardBody: React.FC<ReviewCardBodyProps> = ({
  header,
  speciesName,
  scientificName,
  speciesTrailing,
  countTrailing,
  count,
  countTitle,
  strikethrough,
  footer,
  actions,
}) => {
  const { t } = useTranslation();
  const valueClasses = [
    "text-sm font-semibold text-storm-blue dark:text-primary-off-white",
    strikethrough ? tailwindClassnames.resolvedTextStrike : "",
  ]
    .filter(Boolean)
    .join(" ");
  const showScientific = Boolean(
    scientificName && scientificName !== speciesName,
  );
  const hasCount = count !== null && count !== undefined;
  const showCountLine = hasCount || countTrailing != null;
  const speciesTitle = showScientific
    ? `${speciesName} (${scientificName})`
    : speciesName;
  return (
    <>
      {header}
      <div className="flex items-baseline gap-2">
        <span className={labelClasses}>{t("Species")}</span>
        {/* Name + Latin truncate together as one line so a long pair ellipsizes
            cleanly; the full string stays available on hover. */}
        <span className="min-w-0 flex-1 truncate" title={speciesTitle}>
          <span className={valueClasses}>{speciesName}</span>
          {showScientific && (
            <span className="ml-1.5 text-xs italic text-off-black-light/60 dark:text-primary-off-white/50">
              {scientificName}
            </span>
          )}
        </span>
        {speciesTrailing}
      </div>
      {showCountLine && (
        <div className="mt-1 flex items-center gap-2">
          {hasCount && <span className={labelClasses}>{t("Count")}</span>}
          {hasCount && (
            <span
              className={`min-w-0 truncate ${valueClasses}`}
              title={countTitle}
            >
              {count}
            </span>
          )}
          {countTrailing != null && (
            <span className="ml-auto flex shrink-0 items-center gap-1">
              {countTrailing}
            </span>
          )}
        </div>
      )}
      {footer}
      {actions}
    </>
  );
};

export default ReviewCardBody;
